import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { GraduationCap, Sparkle, Rocket, Check } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const THEMES = {
  scholar: { bg: "#faf8f3", primary: "#1f3d33", accent: "#b8862b", font: "Georgia, serif" },
  campus: { bg: "#fffaf3", primary: "#d97706", accent: "#0ea5e9", font: "Poppins, sans-serif" },
  lab: { bg: "#f5f9fa", primary: "#0d9488", accent: "#155e75", font: "Inter, sans-serif" },
};
const MODULES = [
  { v: "about", l: "About" }, { v: "courses", l: "Courses" }, { v: "faculty", l: "Faculty" },
  { v: "gallery", l: "Gallery" }, { v: "alumni", l: "Alumni" }, { v: "events", l: "Events" }, { v: "contact", l: "Contact" },
];

export default function SandboxBuilder() {
  useSeo("Try the builder", "Design an institution website live — no signup needed.");
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState({
    name: "Your Institution", type: "University", category: "university", country: "", location: "",
    tagline: "Where curiosity meets community.", theme: "scholar",
    hero: { headline: "Welcome to Your Institution", subtext: "A place of learning, discovery and lifelong friendships." },
    about: "Tell your story here — your history, your mission, and what makes your community special.",
    modules: ["about", "courses", "faculty", "gallery", "alumni", "contact"],
  });
  const [busy, setBusy] = useState(false);
  const t = THEMES[d.theme];
  const toggle = (m) => setD((s) => ({ ...s, modules: s.modules.includes(m) ? s.modules.filter((x) => x !== m) : [...s.modules, m] }));

  const publish = async () => {
    const payload = { ...d, modules: ["hero", ...d.modules] };
    if (user) {
      setBusy(true);
      try {
        const { data } = await api.post("/sandbox/publish", payload);
        toast.success("Your website is live!");
        nav(data.builder_path);
      } catch (e) { toast.error("Could not publish"); }
      finally { setBusy(false); }
    } else {
      try { localStorage.setItem("sandbox_draft", JSON.stringify(payload)); } catch { /* ignore */ }
      toast.success("Nice! Create a free account to publish it.");
      nav("/register?from=sandbox");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-10">
      <div className="text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary/10 px-3 py-1 text-xs font-medium text-secondary"><Sparkle size={13} weight="fill" /> Live preview · no signup</span>
        <h1 className="mt-3 font-heading text-4xl font-light tracking-tight">Design your institution website</h1>
        <p className="mt-2 text-muted-foreground">Edit on the left, watch it come alive on the right. Publish when you're ready.</p>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        {/* editor */}
        <div className="space-y-4" data-testid="sandbox-editor">
          <div><Label>Institution name</Label><Input className="mt-1.5" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value, hero: { ...d.hero, headline: `Welcome to ${e.target.value}` } })} data-testid="sandbox-name" /></div>
          <div><Label>Tagline</Label><Input className="mt-1.5" value={d.tagline} onChange={(e) => setD({ ...d, tagline: e.target.value })} data-testid="sandbox-tagline" /></div>
          <div><Label>Hero subtext</Label><Textarea rows={2} className="mt-1.5" value={d.hero.subtext} onChange={(e) => setD({ ...d, hero: { ...d.hero, subtext: e.target.value } })} /></div>
          <div><Label>About</Label><Textarea rows={4} className="mt-1.5" value={d.about} onChange={(e) => setD({ ...d, about: e.target.value })} data-testid="sandbox-about" /></div>
          <div>
            <Label>Theme</Label>
            <div className="mt-1.5 flex gap-2">
              {Object.keys(THEMES).map((k) => (
                <button key={k} onClick={() => setD({ ...d, theme: k })} className={`flex items-center gap-2 rounded-full border-2 px-3 py-1.5 text-sm capitalize ${d.theme === k ? "border-secondary" : "border-border"}`} data-testid={`sandbox-theme-${k}`}>
                  <span className="h-4 w-4 rounded-full" style={{ background: THEMES[k].primary }} /> {k}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Sections</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {MODULES.map((m) => (
                <button key={m.v} onClick={() => toggle(m.v)} className={`rounded-full border px-3 py-1.5 text-sm ${d.modules.includes(m.v) ? "border-secondary bg-secondary text-secondary-foreground" : "border-border text-muted-foreground"}`}>
                  {d.modules.includes(m.v) && <Check size={12} className="mr-1 inline" />}{m.l}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={publish} disabled={busy} size="lg" className="w-full rounded-full gap-2" data-testid="sandbox-publish">
            <Rocket size={18} /> {busy ? "Publishing…" : user ? "Publish my website" : "Publish — create free account"}
          </Button>
        </div>

        {/* live preview */}
        <div className="lg:sticky lg:top-24">
          <div className="overflow-hidden rounded-2xl border border-border/60 shadow-xl" style={{ background: t.bg, fontFamily: t.font }} data-testid="sandbox-preview">
            <div className="flex items-center gap-2 px-4 py-3" style={{ background: t.primary }}>
              <span className="grid h-7 w-7 place-items-center rounded-md bg-white/20 text-white"><GraduationCap size={15} weight="fill" /></span>
              <span className="text-sm font-semibold text-white" style={{ fontFamily: t.font }}>{d.name}</span>
            </div>
            <div className="px-6 py-10 text-center text-white" style={{ background: t.primary }}>
              <h2 className="text-2xl font-bold" style={{ fontFamily: t.font }}>{d.hero.headline}</h2>
              <p className="mx-auto mt-2 max-w-md text-sm opacity-90">{d.hero.subtext}</p>
              <span className="mt-4 inline-block rounded-full px-4 py-2 text-xs font-medium" style={{ background: t.accent }}>Learn more</span>
            </div>
            <div className="p-6">
              {d.modules.includes("about") && (
                <><h3 className="text-lg font-semibold" style={{ color: t.primary, fontFamily: t.font }}>About</h3>
                <p className="mt-2 whitespace-pre-line text-sm opacity-80">{d.about}</p></>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {d.modules.filter((m) => m !== "about").map((m) => (
                  <span key={m} className="rounded-full px-3 py-1 text-xs capitalize" style={{ background: `${t.accent}22`, color: t.primary }}>{m}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
